import BackendService from '../../../services/admin/BackendService';
import { FundOpportunityWithInvestor } from './contactFundOpportunityKanbanTypes';

type PrerequisiteRunner = (
  run: (options: { overrideFundStatusRestriction?: boolean }) => Promise<void>,
  successCallback: () => void,
  operationName: string,
) => void;

export interface StageActionDeps {
  onError: (message: string) => void;
  onReloadContact?: () => void;
  reloadOpportunities: () => void;
  handleWithPrerequisites: PrerequisiteRunner;
  collectInvestment: (label: string, currentInvestmentAmount?: number) => Promise<number>;
  service?: BackendService;
}

export function promoteOpportunity(
  opportunity: FundOpportunityWithInvestor,
  deps: StageActionDeps,
): void {
  const service = deps.service ?? new BackendService();
  const investor = opportunity.investor;

  if (!investor) {
    deps.onError('No investor data found');
    return;
  }

  if (investor.status === 'CANDIDATE') {
    deps.handleWithPrerequisites(
      (opts) => service.promoteToLead(investor.id, opts),
      () => {
        deps.reloadOpportunities();
        deps.onReloadContact?.();
      },
      'Promote to Lead',
    );
    return;
  }

  if (investor.status === 'LEAD' && !investor.eoiSubmitted) {
    void deps
      .collectInvestment('Indicative Investment', investor.indicativeInvestment)
      .then((amount) => {
        deps.handleWithPrerequisites(
          (opts) => service.submitEOI(investor.id, amount, opts),
          () => {
            deps.reloadOpportunities();
          },
          'Submit EOI',
        );
      });
    return;
  }

  if (investor.status === 'LEAD' && investor.eoiSubmitted) {
    void deps
      .collectInvestment('Preferred Investment', investor.indicativeInvestment)
      .then((amount) => {
        deps.handleWithPrerequisites(
          (opts) => service.promoteToOpportunity(investor.id, amount, opts),
          () => {
            deps.reloadOpportunities();
          },
          'Promote to Opportunity',
        );
      });
    return;
  }

  if (investor.status === 'OPPORTUNITY') {
    void deps
      .collectInvestment('Committed Investment', investor.indicativeInvestment)
      .then((amount) => {
        deps.handleWithPrerequisites(
          (opts) => service.commitInvestment(investor.id, amount, opts),
          () => {
            deps.reloadOpportunities();
          },
          'Commit Investment',
        );
      });
  }
}

export function demoteOpportunity(
  opportunity: FundOpportunityWithInvestor,
  deps: StageActionDeps,
): void {
  const service = deps.service ?? new BackendService();
  const investor = opportunity.investor;

  if (!investor) {
    deps.onError('No investor data found');
    return;
  }

  if (investor.status === 'LEAD' && investor.eoiSubmitted) {
    deps.handleWithPrerequisites(
      (opts) => service.withdrawEOI(investor.id, opts),
      () => {
        deps.reloadOpportunities();
      },
      'Withdraw EOI',
    );
    return;
  }

  if (investor.status === 'LEAD') {
    deps.handleWithPrerequisites(
      (opts) => service.demoteToCandidate(investor.id, opts),
      () => {
        deps.reloadOpportunities();
        deps.onReloadContact?.();
      },
      'Demote to Candidate',
    );
    return;
  }

  if (investor.status === 'OPPORTUNITY') {
    deps.handleWithPrerequisites(
      (opts) => service.demoteToLead(investor.id, opts),
      () => {
        deps.reloadOpportunities();
      },
      'Demote to Lead',
    );
    return;
  }

  if (investor.status === 'COMMITTED_INVESTOR') {
    deps.handleWithPrerequisites(
      (opts) => service.demoteToOpportunity(investor.id, opts),
      () => {
        deps.reloadOpportunities();
      },
      'Demote to Opportunity',
    );
  }
}

export function deleteOpportunity(
  opportunity: FundOpportunityWithInvestor,
  deps: Pick<StageActionDeps, 'onError' | 'reloadOpportunities' | 'service'>,
): void {
  const service = deps.service ?? new BackendService();
  const investor = opportunity.investor;

  if (!investor) {
    deps.onError('No investor data found');
    return;
  }

  if (investor.status === 'CANDIDATE') {
    service
      .deleteInvestor(investor.id)
      .then(() => {
        deps.reloadOpportunities();
      })
      .catch((error: Error) => {
        console.error('Error deleting investor:', error);
        deps.onError(error.message || 'Failed to remove investor');
      });
    return;
  }

  deps.onError('Can only delete investors in CANDIDATE status');
}
